import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import {
  formatEpoch,
  formatEpochDate,
  formatEpochTime,
  getStatus,
  looksLikeVideo,
} from '../config';

/** Anything closer than this to the bottom counts as "following the thread". */
const NEAR_BOTTOM_PX = 120;

/** Composer textarea grows up to this height, then scrolls internally. */
const MAX_COMPOSER_PX = 128;

// Generic scalar field labels — covers edits the DESIGNER makes on this same
// shared creative (img_urls, video_urls, …), which a content writer will see
// in this same feed. This panel's own field edits never appear as scalar
// `fields` changes — see `content_variations` handling in FieldsLine below.
const FIELD_LABELS = {
  img_urls: 'design image',
  video_urls: 'design video',
  creative_name: 'creative name',
  head_line: 'headline',
  description: 'description',
  text_in_link: 'link text',
  click_url: 'click URL',
};

/** `changes` arrives as jsonb — usually an array, occasionally a JSON string. */
const parseChanges = (changes) => {
  if (!changes) return [];
  if (Array.isArray(changes)) return changes;
  if (typeof changes === 'string') {
    try {
      const parsed = JSON.parse(changes);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const initialsOf = (name = '') =>
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const prettySize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Status + field changes are metadata, not conversation: one quiet centred line
 * each, deliberately without any bubble weight so the messages stay dominant.
 */
const SystemLine = ({ icon, children, at }) => (
  <div className="my-2 flex justify-center px-6">
    <p className="flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-center text-sm leading-snug text-slate-400">
      <SlIcon library="lucide" name={icon} className="shrink-0" style={{ fontSize: '13px' }} />
      {children}
      <span className="text-slate-300" title={formatEpoch(at)}>
        · {formatEpochTime(at)}
      </span>
    </p>
  </div>
);

/**
 * Status lines can come from either side of this shared feed:
 *  - `content_status` is OUR transition (Assigned/Picked/Submitted/…) — we
 *    know the labels, so render the arrow.
 *  - `status` is the DESIGNER's transition, in their own 1-8 namespace. This
 *    panel does not own that label set and must not duplicate or guess it,
 *    so it renders the log's message only, never a fabricated label.
 */
const StatusLine = ({ log, changes }) => {
  const own = changes.find((c) => c?.type === 'content_status');
  if (own) {
    const from = getStatus(own.previous).label;
    const to = getStatus(own.updated).label;
    return (
      <SystemLine icon="git-commit-horizontal" at={log.created_time}>
        <span className="font-medium text-slate-500">
          {from} <span className="text-slate-300">→</span> {to}
        </span>
        {log.message ? <span>{log.message}</span> : null}
      </SystemLine>
    );
  }

  const foreign = changes.find((c) => c?.type === 'status');
  return (
    <SystemLine icon="git-commit-horizontal" at={log.created_time}>
      <span className="font-medium text-slate-500">
        {foreign ? 'Design status updated' : log.message || 'Status updated'}
      </span>
      {foreign && log.message ? <span>{log.message}</span> : null}
    </SystemLine>
  );
};

/**
 * Field-change lines, same split as above:
 *  - `content_variations` is OUR draft submission — the payload is an array
 *    of variation objects, not a scalar, so this is summarised as a count
 *    ("submitted N draft(s)") rather than diffed like a normal field.
 *  - anything else is a scalar field the DESIGNER changed on the shared
 *    creative (img_urls, video_urls, …), rendered with the same generic
 *    labels the designer panel uses for its own field-change lines.
 */
/**
 * Small, clickable media preview for a `fields` change (e.g. a designer's
 * `img_urls`/`video_urls` update). Deliberately a lighter-weight sibling of
 * `Attachment` below — a centred system line shouldn't carry a full
 * message-bubble-sized (`max-h-56`) image — but it follows the same
 * zoom-callback pattern so the thumbnail is a real, tappable preview.
 */
const FieldThumbnail = ({ url, isVideo, onZoom }) => {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600"
      >
        <SlIcon library="lucide" name="paperclip" style={{ fontSize: '11px' }} />
        Open attachment
      </a>
    );
  }

  if (isVideo) {
    return (
      <video
        src={url}
        controls
        onError={() => setBroken(true)}
        className="h-20 w-28 rounded-lg border border-black/5 bg-black object-cover"
      />
    );
  }

  return (
    <img
      src={url}
      alt="attachment"
      loading="lazy"
      onError={() => setBroken(true)}
      onClick={() => onZoom?.(url, false)}
      className="h-20 w-28 cursor-zoom-in rounded-lg border border-black/5 object-cover"
    />
  );
};

const FieldsLine = ({ log, changes, onZoom }) => {
  const variations = changes.find((c) => c?.type === 'content_variations');
  if (variations) {
    const count = Array.isArray(variations.updated) ? variations.updated.length : 0;
    return (
      <SystemLine icon="file-clock" at={log.created_time}>
        <span className="font-medium text-slate-500">
          {log.message || `Submitted ${count} draft${count === 1 ? '' : 's'}`}
        </span>
      </SystemLine>
    );
  }

  const names = changes.map((c) => FIELD_LABELS[c?.type] || c?.type).filter(Boolean);
  const media = changes.filter(
    (c) => (c?.type === 'img_urls' || c?.type === 'video_urls') && c?.updated
  );

  return (
    <SystemLine icon="file-clock" at={log.created_time}>
      <span className="font-medium text-slate-500">{log.message || 'Updated the creative'}</span>
      {names.length ? <span>({names.join(', ')})</span> : null}
      {media.length ? (
        <span className="mt-1 flex w-full flex-wrap items-center justify-center gap-1.5">
          {media.map((c, i) => (
            <FieldThumbnail
              key={`${log.id ?? log.created_time}-media-${i}`}
              url={c.updated}
              isVideo={c.type === 'video_urls'}
              onZoom={onZoom}
            />
          ))}
        </span>
      ) : null}
    </SystemLine>
  );
};

const Attachment = ({ url, isVideo, onZoom }) => {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-600"
      >
        <SlIcon library="lucide" name="paperclip" style={{ fontSize: '12px' }} />
        Open attachment
      </a>
    );
  }

  if (isVideo) {
    return (
      <video
        src={url}
        controls
        onError={() => setBroken(true)}
        className="mt-1 max-h-56 w-full rounded-lg border border-black/5 bg-black"
      />
    );
  }

  return (
    <img
      src={url}
      alt="attachment"
      loading="lazy"
      onError={() => setBroken(true)}
      onClick={() => onZoom?.(url, false)}
      className="mt-1 max-h-56 cursor-zoom-in rounded-lg border border-black/5 object-cover"
    />
  );
};

const MessageBubble = ({ log, changes, mine, grouped, onZoom }) => {
  const attachments = log.media_url
    ? [{ url: log.media_url, isVideo: log.media_type === 'video' || (!log.media_type && looksLikeVideo(log.media_url)) }]
    : changes
        .filter((c) => c?.type === 'attachment' && c?.updated)
        .map((c) => ({ url: c.updated, isVideo: looksLikeVideo(c.updated) }));

  return (
    <div
      className={`flex ${mine ? 'justify-end' : 'justify-start'} ${grouped ? 'mt-[3px]' : 'mt-3'}`}
    >
      {!mine && (
        <div className="mr-2 w-7 shrink-0 self-end">
          {!grouped && (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
              {initialsOf(log.user_name)}
            </div>
          )}
        </div>
      )}
      <div className="min-w-0 max-w-[82%]">
        {!grouped && !mine && (
          <p className="mb-0.5 ml-1 flex items-baseline gap-1.5 text-xs text-slate-500">
            <span className="font-semibold text-slate-600">{log.user_name || 'Unknown'}</span>
            <span className="capitalize text-slate-400">{log.user_type}</span>
          </p>
        )}
        <div
          className={`px-3 py-2 text-[15px] leading-relaxed shadow-sm ${
            mine
              ? `rounded-2xl bg-sky-600 text-white ${grouped ? 'rounded-tr-md' : ''} rounded-br-md`
              : `rounded-2xl border border-slate-200 bg-white text-slate-800 ${
                  grouped ? 'rounded-tl-md' : ''
                } rounded-bl-md`
          }`}
        >
          {log.message ? (
            <p className="whitespace-pre-wrap break-words">{log.message}</p>
          ) : null}
          {attachments.map((a, i) => (
            <Attachment key={`${log.id}-att-${i}`} url={a.url} isVideo={a.isVideo} onZoom={onZoom} />
          ))}
          <div
            className={`mt-0.5 text-right text-[11px] leading-tight ${
              mine ? 'text-sky-100/90' : 'text-slate-400'
            }`}
            title={formatEpoch(log.created_time)}
          >
            {formatEpochTime(log.created_time)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Conversation + activity feed for one creative.
 *
 * All incoming activity arrives through the parent's `applyIncoming` seam —
 * this component only renders and composes. There is no polling here.
 *
 * The composer carries two deliberately different affordances, mirroring the
 * designer panel's attachment-vs-deliverable split:
 *
 *  - Paperclip → ATTACHMENT. `onSendAttachment` writes a log row only. It
 *    never touches `variations` and never moves the status. No status gate.
 *  - Submit drafts → DELIVERABLE. `onOpenSubmit` just opens the drawer's
 *    single `SubmitVariationsModal` (which lives in the parent, one instance),
 *    which is what writes the `variations` field and moves the content
 *    status. Gated separately on `canSubmitDrafts` — a creative the writer
 *    has not picked yet can be talked about but not delivered into.
 *
 * The modal is NOT mounted here: this component only asks for it.
 */
const ThreadPanel = ({
  logs,
  loading,
  error,
  onRetry,
  canSend,
  canSubmitDrafts,
  lockedReason,
  onSend,
  onSendAttachment,
  onOpenSubmit,
  onZoom,
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState('');
  const [nearBottom, setNearBottom] = useState(true);
  const [missed, setMissed] = useState(0);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileRef = useRef(null);
  const mountedRef = useRef(true);
  const submittingRef = useRef(false);
  const nearBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  const seenLenRef = useRef(0);
  const logsLenRef = useRef(0);
  logsLenRef.current = logs.length;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const items = useMemo(
    () =>
      logs.map((log) => ({
        log,
        changes: parseChanges(log?.changes),
      })),
    [logs]
  );

  /* ---------------------------------------------------------------- */
  /* Scrolling                                                        */
  /* ---------------------------------------------------------------- */

  const scrollToBottom = useCallback((smooth) => {
    const el = scrollRef.current;
    if (!el) return;
    if (smooth) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    else el.scrollTop = el.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = distance < NEAR_BOTTOM_PX;
    if (nearBottomRef.current === near) return;
    nearBottomRef.current = near;
    setNearBottom(near);
    if (near) {
      seenLenRef.current = logsLenRef.current;
      setMissed(0);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (!logs.length) {
      didInitialScrollRef.current = false;
      seenLenRef.current = 0;
      nearBottomRef.current = true;
      setNearBottom(true);
      setMissed(0);
      return;
    }

    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      seenLenRef.current = logs.length;
      nearBottomRef.current = true;
      setNearBottom(true);
      setMissed(0);
      scrollToBottom(false);
      return;
    }

    if (!nearBottomRef.current) {
      const behind = logs.length - seenLenRef.current;
      setMissed(behind > 0 ? behind : 0);
      return;
    }

    seenLenRef.current = logs.length;
    scrollToBottom(false);
    // Intentionally keyed on the count only — logs is a new array on every merge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs.length]);

  const jumpToLatest = useCallback(() => {
    nearBottomRef.current = true;
    seenLenRef.current = logsLenRef.current;
    setNearBottom(true);
    setMissed(0);
    scrollToBottom(true);
  }, [scrollToBottom]);

  /* ---------------------------------------------------------------- */
  /* Composer                                                        */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_PX)}px`;
  }, [text]);

  useEffect(() => {
    if (!pendingFile) {
      setPendingUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(pendingFile);
    setPendingUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const busy = sending || attaching;
  const pendingIsVideo = !!pendingFile && String(pendingFile.type || '').startsWith('video/');

  const handleUploadProgress = useCallback((e) => {
    if (!e?.total || !mountedRef.current) return;
    setProgress(Math.min(99, Math.round((e.loaded * 100) / e.total)));
  }, []);

  const clearPending = useCallback(() => {
    if (submittingRef.current) return;
    setPendingFile(null);
  }, []);

  const stagePending = useCallback((file) => {
    if (!file || submittingRef.current) return;
    setPendingFile(file);
    setProgress(0);
  }, []);

  const handlePaste = useCallback(
    (e) => {
      const list = e.clipboardData?.items;
      if (!list) return;
      const items = Array.from(list);
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const original = item.getAsFile();
        if (!original) continue;
        const type = String(original.type || '');
        if (!type.startsWith('image/') && !type.startsWith('video/')) continue;
        const ext = type.split('/')[1] || 'bin';
        const file = new File([original], `pasted-${Date.now()}.${ext}`, { type });
        e.preventDefault();
        stagePending(file);
        return;
      }
    },
    [stagePending]
  );

  const handlePick = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      stagePending(file);
    },
    [stagePending]
  );

  const submit = useCallback(async () => {
    if (!canSend || submittingRef.current) return;
    const value = text.trim();
    const file = pendingFile;
    if (!file && !value) return;

    submittingRef.current = true;
    try {
      if (file) {
        setAttaching(true);
        setProgress(0);
        const ok = await onSendAttachment(file, value, handleUploadProgress);
        if (!mountedRef.current) return;
        setAttaching(false);
        setProgress(0);
        if (ok) {
          setPendingFile(null);
          setText('');
        }
        return;
      }

      setSending(true);
      const ok = await onSend(value);
      if (!mountedRef.current) return;
      setSending(false);
      if (ok) setText('');
    } finally {
      submittingRef.current = false;
    }
  }, [canSend, text, pendingFile, onSend, onSendAttachment, handleUploadProgress]);

  const canSubmit = !!pendingFile || !!text.trim();

  // Gated independently of messaging: `canSubmitDrafts` comes from the
  // config's `canSubmitVariations`, `canSend` from its `canMessage`. On this
  // panel messaging has no status gate at all, but the draft-submission
  // button still respects the status matrix — a writer can chat about an
  // Assigned creative without being able to deliver drafts before picking it.
  const showSubmitButton = !!canSubmitDrafts && typeof onOpenSubmit === 'function';

  let lastDay = null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50">
      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto px-3 py-2">
          {loading && logs.length === 0 && (
            <div className="space-y-3 py-4">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`h-12 animate-pulse rounded-2xl bg-slate-200 ${
                    i % 2 ? 'ml-auto w-2/3' : 'w-3/5'
                  }`}
                />
              ))}
            </div>
          )}

          {error && logs.length === 0 && (
            <div className="my-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-center">
              <p className="text-sm text-rose-800">{error}</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-rose-700"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && logs.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-slate-700">No activity yet</p>
              <p className="mt-1 text-xs text-slate-500">
                Ask the sales person a question or submit your first draft — everything shows up
                here.
              </p>
            </div>
          )}

          {items.map(({ log, changes }, idx) => {
            const day = formatEpochDate(log.created_time);
            const showDay = day !== lastDay;
            lastDay = day;

            const prev = idx > 0 ? items[idx - 1].log : null;
            // `is_mine` is computed by the server and must NOT be re-derived
            // client-side: a display name can collide between two writers,
            // and a name-matching fallback would misattribute logs. If the
            // flag is absent the bubble renders as someone else's, which is
            // the honest default.
            const mine = log.is_mine === true;
            const grouped =
              !showDay &&
              prev &&
              prev.activity_type === 'message' &&
              log.activity_type === 'message' &&
              prev.user_name === log.user_name &&
              prev.user_type === log.user_type;

            return (
              <React.Fragment key={log.id ?? `${log.created_time}-${idx}`}>
                {showDay && (
                  <div className="my-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {day}
                    </span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>
                )}
                {log.activity_type === 'status' && <StatusLine log={log} changes={changes} />}
                {log.activity_type === 'fields' && (
                  <FieldsLine log={log} changes={changes} onZoom={onZoom} />
                )}
                {log.activity_type === 'message' && (
                  <MessageBubble
                    log={log}
                    changes={changes}
                    mine={mine}
                    grouped={grouped}
                    onZoom={onZoom}
                  />
                )}
              </React.Fragment>
            );
          })}
          <div className="h-1" />
        </div>

        {!nearBottom && logs.length > 0 && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="absolute bottom-3 left-1/2 inline-flex max-w-[calc(100%-1.5rem)] -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-lg transition hover:bg-slate-50"
          >
            <SlIcon library="lucide" name="chevron-down" style={{ fontSize: '14px' }} />
            Jump to latest
            {missed > 0 ? <span className="text-sky-600">· {missed} new</span> : null}
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-3 py-2.5">
        {canSend ? (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm transition focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
              {pendingFile && (
                <div className="m-1.5 mb-0 flex items-center gap-2.5 rounded-xl bg-slate-50 p-2">
                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {pendingIsVideo ? (
                      <video src={pendingUrl} muted className="h-full w-full object-cover" />
                    ) : (
                      <img
                        src={pendingUrl}
                        alt="attachment preview"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium text-slate-700"
                      title={pendingFile.name}
                    >
                      {pendingFile.name}
                    </p>
                    <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-400">
                      {prettySize(pendingFile.size)} · Reference file · does not submit your drafts
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearPending}
                    disabled={busy}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-white hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <SlIcon library="lucide" name="x" style={{ fontSize: '15px' }} />
                  </button>
                </div>
              )}

              {attaching && (
                <div className="px-3 pt-2">
                  <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-slate-500">
                    <span>Attaching… {progress}%</span>
                    <SlIcon
                      library="lucide"
                      name="paperclip"
                      className="text-slate-400"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-sky-600 transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-end gap-1 p-1.5">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={handlePick}
                />
                {showSubmitButton && (
                  <button
                    type="button"
                    onClick={() => onOpenSubmit()}
                    disabled={busy}
                    title="Submit your drafts — sends them for review"
                    aria-label="Submit your drafts — sends them for review"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 ring-1 ring-inset ring-sky-200 transition hover:bg-sky-100 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <SlIcon library="lucide" name="file-text" style={{ fontSize: '16px' }} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  title="Attach a reference file — this does not submit your drafts"
                  aria-label="Attach a reference file — this does not submit your drafts"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <SlIcon library="lucide" name="paperclip" style={{ fontSize: '17px' }} />
                </button>
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  placeholder={pendingFile ? 'Add a caption… (optional)' : 'Write a message…'}
                  className="min-h-[36px] min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-[15px] leading-snug text-slate-800 outline-none focus:ring-0 placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || busy}
                  aria-label="Send"
                  title="Send"
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition ${
                    busy
                      ? 'cursor-wait bg-sky-600 text-white'
                      : canSubmit
                      ? 'bg-sky-600 text-white hover:bg-sky-700'
                      : 'cursor-not-allowed bg-slate-200 text-slate-400'
                  }`}
                >
                  {busy ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : (
                    <SlIcon library="lucide" name="send" style={{ fontSize: '15px' }} />
                  )}
                </button>
              </div>
            </div>

            <p className="mt-1.5 px-1 text-xs text-slate-400">
              Enter to send · Shift+Enter for a new line
            </p>
          </>
        ) : (
          <p className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500">
            <SlIcon library="lucide" name="lock" style={{ fontSize: '13px' }} />
            {lockedReason || 'This conversation is closed.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default ThreadPanel;
